import React from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/common/Layout';
import SEO from '../components/common/SEO';
import { Link } from 'react-router-dom';
import { Button } from '@headlessui/react';

const AboutPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <Layout>
      <SEO 
        title={t('about.pageTitle', 'About Nemlingo | Our Story and Mission')}
        description={t('about.pageDescription', 'Learn about the story behind Nemlingo, our founder\'s language learning journey, and our mission to make language learning accessible to everyone.')}
        keywords={t('about.pageKeywords', 'about nemlingo, language learning story, language journaling, shadowing technique, language learning method')}
      />
      
      <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl sm:tracking-tight">
            {t('about.title', 'Our Story')}
          </h1>
          <p className="mt-5 max-w-xl mx-auto text-xl text-gray-500">
            {t('about.subtitle', 'The journey from language learner to language learning innovator')}
          </p>
        </div>
        
        <div className="">
            <div className="bg-gradient-to-r from-primary-50 to-white p-6 rounded-lg mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('about.innovatorTitle', 'From Passionate Learner to Language Learning Innovator')}</h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">{t('about.introText', 'At Nemlingo, we believe that language learning doesn\'t have to be overwhelming. It can be simple, joyful, and incredibly effective, especially when done consistently in small, meaningful steps. Our story began not with a business plan, but with a notebook, a voice recorder, and a passionate language learner determined to improve every single day.')}</p>
              
              <p className="text-gray-700 mb-6 leading-relaxed">{t('about.journeyText', 'Like many learners, I struggled with remembering vocabulary, speaking confidently, and staying motivated. But everything changed when I committed to a simple daily habit: journaling in English and shadowing native speakers for just 10 minutes a day. The results were powerful. My thinking became sharper, my speaking more fluent, and my connection with the language deeper. And it didn\'t stop with English. Using the same method, I went on to master Japanese and Danish as well, and today, I can speak all three foreign languages fluently. That\'s when I knew this approach wasn\'t just effective. It was transformative. And that\'s when the idea for Nemlingo was born.')}</p>
            </div>
            
            <div className="bg-white shadow-sm rounded-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">{t('about.communityTitle', 'A Community Built on Progress, Not Perfection')}</h3>
              
              <p className="text-gray-700 mb-4">{t('about.communityText', 'Nemlingo isn\'t just a tool. It\'s a community of learners who believe in the magic of daily progress. We focus on two of the most natural yet underrated methods in language acquisition:')}</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="bg-primary-light p-4 rounded-lg">
                  <div className="flex items-center mb-2">
                    <span className="text-2xl mr-2">📝</span>
                    <h4 className="font-medium text-gray-900">Journaling</h4>
                  </div>
                  <p className="text-gray-600 pl-8">{t('about.journalingMethod', 'Reflect, write, and turn thoughts into fluent expression.')}</p>
                </div>
                
                <div className="bg-primary-light p-4 rounded-lg">
                  <div className="flex items-center mb-2">
                    <span className="text-2xl mr-2">🎤</span>
                    <h4 className="font-medium text-gray-900">Shadowing</h4>
                  </div>
                  <p className="text-gray-600 pl-8">{t('about.shadowingMethod', 'Train your ears and mouth by mimicking native pronunciation, rhythm, and tone.')}</p>
                </div>
              </div>
              
              <p className="text-gray-700 italic border-l-4 border-primary-300 pl-4 py-2">{t('about.methodsResult', 'These methods are proven to activate both receptive and productive language skills, and when done consistently, they compound over time—just like investing. We call it "incremental fluency." Small daily actions that lead to big, lasting results.')}</p>
            </div>
          </div>
        </div>
        <div className="pt-4 mb-16">
          <div className="flex flex-col md:flex-row items-center justify-center gap-8">
            <div className="md:w-1/4">
              <div className="aspect-w-1 aspect-h-1 rounded-lg overflow-hidden bg-gray-100 max-w-[180px] mx-auto">
                <img 
                  src="/founder.jpg" 
                  alt={t('about.founderImageAlt', 'Tracy Tra Tran, Founder of Nemlingo')} 
                  className="object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = 'https://via.placeholder.com/400x400?text=Tracy+Tra+Tran';
                  }}
                />
              </div>
            </div>
            
            <div className="md:w-3/4 text-center md:text-left">
              <h3 className="text-xl font-medium text-gray-900">Tracy Tra Tran</h3>
              <p className="text-gray-500 mb-3">{t('about.founderTitle', 'Người sáng lập & Người đam mê ngôn ngữ')}</p>
              <p className="text-gray-600 italic mb-4">"Language learning changed my life, and I hope Nemlingo can do the same for you."</p>
              {/* <div className="flex justify-center md:justify-start space-x-4">
                <a
                  href="https://www.facebook.com/tracytratran"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-blue-600 transition-colors"
                  aria-label="Facebook"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
                  </svg>
                </a>
                <a
                  href="https://www.youtube.com/@tracytratran"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-red-600 transition-colors"
                  aria-label="YouTube"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                  </svg>
                </a>
                <a
                  href="https://www.linkedin.com/in/tracytratran/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-blue-600 transition-colors"
                  aria-label="LinkedIn"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452z" />
                  </svg>
                </a>
              </div> */}
            </div>
          </div>
        </div>
        
        <div className="bg-white shadow-md rounded-lg p-8 mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('about.innovationTitle', 'Powered by Innovation')}</h2>
          <p className="text-gray-600 mb-6">{t('about.innovationText', 'Nemlingo combines traditional learning wisdom with AI-driven feedback and smart tracking tools to help you stay accountable, motivated, and supported. You\'ll get personalized suggestions, pronunciation guidance, and gentle nudges to keep going—because we know the biggest challenge in language learning is often consistency.')}</p>
          
          <p className="text-gray-600 mb-6">{t('about.builtByLearnersText', 'And unlike generic apps, Nemlingo was built by learners, for learners. We know what it\'s like to freeze mid-sentence, forget a word at the worst time, or feel alone in the process. That\'s why our platform is designed to feel like a friendly companion on your language journey—not a classroom.')}</p>
          
          <h3 className="text-xl font-bold text-gray-900 mb-3 mt-8">{t('about.missionTitle', 'Our Mission')}</h3>
          <p className="text-gray-600 mb-6">{t('about.missionText', 'Our mission is simple: To make language learning accessible, empowering, and sustainable for anyone, anywhere. Whether you\'re learning for school, work, travel, or personal growth, Nemlingo is here to guide you, one word, one sentence, one voice at a time.')}</p>
        </div>
        
        <div className="text-center mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">{t('about.whyNemlingoTitle', 'Why Nemlingo Works')}</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 mx-auto bg-primary-100 rounded-full flex items-center justify-center mb-4">
                <svg className="h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('about.benefit1Title', 'Personalized Learning')}</h3>
              <p className="text-gray-600">{t('about.benefit1Description', 'Learn vocabulary and phrases relevant to your life through your own journal entries.')}</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 mx-auto bg-primary-100 rounded-full flex items-center justify-center mb-4">
                <svg className="h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('about.benefit2Title', 'Complete Approach')}</h3>
              <p className="text-gray-600">{t('about.benefit2Description', 'Improve all four language skills: reading, writing, listening, and speaking.')}</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 mx-auto bg-primary-100 rounded-full flex items-center justify-center mb-4">
                <svg className="h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('about.benefit3Title', 'Sustainable Progress')}</h3>
              <p className="text-gray-600">{t('about.benefit3Description', 'Build a daily habit that leads to steady, long-term language improvement.')}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-primary-light rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {t('about.joinUsTitle', 'Join Our Language Learning Community')}
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            {t('about.joinUsDescription', 'Whether you\'re just starting your language journey or looking to reignite your motivation, Nemlingo is the place for you. Join our growing community of language learners today.')}
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <Button
              onClick={() => window.open('https://docs.google.com/forms/d/e/1FAIpQLSd-pn7MaMQISX8x265Rwwe4cR2MtreDmuSa7lmORsMqJmUQCQ/viewform?usp=header', '_blank')}
              className="bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-6 rounded-lg transition duration-300"
            >
              {t('form.joinWaitlist', 'Join Waitlist')}
            </Button>
          </div>
        </div>
    </Layout>
  );
};

export default AboutPage;
